import { html, LitElement, TemplateResult } from 'lit';
import { styles } from '../view/styles';
import {
  getImAttributeChangesStartTypeMapping,
  getImAttributeChangesStatusMapping,
  IntegrityMonitorEventsPanelConfig,
} from '../types';
import { htmlEncode } from '../../../../utils/escape.utils';
import { getFormattedDateTime, TimestampFormat } from '@nirvana/shared/time';
import {
  getIntegrityMonitorChangesNames,
  getIntegrityMonitorResultTexts,
  IntegrityMonitorChangedAttribute,
} from '../../integrityMonitorEventsGrid/types';
import { LinkColor } from '../../../../ui/gzLinkComponent/types';
import { CommonEvents } from '@nirvana/shared/constants';
import { GzLinkWebComponent } from '../../../../ui/gzLinkComponent';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { capitalize } from '@nirvana/shared/common';
import { getSeverityWithBullet } from '../../../common/utils/helpers';
import { GzAccordionWebComponent } from '../../../../ui/gzAccordion';
import { getIntegrityMonitorRulesActionsLabels } from '../../../common/utils/types';
import { translateFn } from '../../../../utils/translator.helper';
import { IMConstants } from '../../../common/utils/IMConstants';

export class IntegrityMonitorDetailsPanelWebComponent extends LitElement {
  private dependencies: any;
  private panelConfig: IntegrityMonitorEventsPanelConfig;
  private oldLabel: string;
  private newLabel: string;
  private copiedLabel: string;
  private clipboardHoverLabel: string;

  public injectDependencies(dependencies: any): void {
    this.dependencies = dependencies;
    this.oldLabel = ' (' + this.dependencies.translateFn('old') + ')';
    this.newLabel = ' (' + this.dependencies.translateFn('new') + ')';
    this.copiedLabel = this.dependencies.translateFn('Copied');
    this.clipboardHoverLabel = this.dependencies.translateFn('Copy to clipboard');
  }

  public setPanelConfig(config: IntegrityMonitorEventsPanelConfig): void {
    this.panelConfig = config;
  }

  static get styles() {
    return styles;
  }

  protected render() {
    if (!this.dependencies || !this.panelConfig) {
      return '';
    }

    return html`
      <div class="details-panel-wrapper">
        <div class="details-panel">${this.renderHeader()} ${this.renderContent()}</div>
      </div>
    `;
  }

  private renderHeader(): TemplateResult {
    return html`
      <div class="details-panel-header">
        <gz-title class="title" fontweight="500" fontsize="16">
          ${this.dependencies.translateFn('Event details')}
        </gz-title>
      </div>
    `;
  }

  private renderContent(): TemplateResult {
    return html`
      ${this.renderGeneralSection()} ${this.renderDetailsSection()} ${this.renderChangesSection()}
      ${this.renderActionsSection()}
    `;
  }

  private renderGeneralSection(): TemplateResult {
    const timezone = this.dependencies.getTimezone();
    const locale = this.dependencies.getUserLanguage();
    const event_time = getFormattedDateTime(TimestampFormat.EVENTS, this.panelConfig.event_time, timezone, locale);
    const endpointLink = new GzLinkWebComponent();

    if (isEmpty(this.panelConfig.endpoint_name)) {
      endpointLink.innerText = isEmpty(this.panelConfig.hostname)
        ? this.dependencies.translateFn('Unknown endpoint')
        : this.panelConfig.hostname;
      endpointLink.disabled = true;
    } else {
      endpointLink.callback = this.sendEndpointShowEventFromPanel.bind(this);
      endpointLink.color = LinkColor.PRIMARY;
      endpointLink.innerText = this.panelConfig.endpoint_name;
    }

    endpointLink.showIcon = false;

    const { bullet, severityLabel } = getSeverityWithBullet(this.dependencies.translateFn, this.panelConfig.severity);

    return html`
      <div class="details-panel-section details-panel-first-section">
        <gz-labeled-content
          class="details-panel-content-title"
          label="${this.dependencies.translateFn('General')}"
          labelfontsize="11"
          labelfontweight="600"
          hasFormLabelWidth="true"
        ></gz-labeled-content>

        <table>
          <gz-labeled-content
            label="${this.dependencies.translateFn('Reason')}"
            labelfontweight="500"
            labelfontsize="12"
          >
            <gz-tooltip class="text-ellipsis-content" tooltiptext="${this.panelConfig.rule_name}" isellipsis="true">
              ${htmlEncode(this.panelConfig.rule_name)}
            </gz-tooltip>
          </gz-labeled-content>

          <gz-labeled-content
            label="${this.dependencies.translateFn('Event date')}"
            labelfontweight="500"
            labelfontsize="12"
          >
            <gz-tooltip class="text-ellipsis-content" tooltiptext="${event_time}" isellipsis="true">
              ${htmlEncode(event_time)}
            </gz-tooltip>
          </gz-labeled-content>

          <gz-labeled-content
            label="${this.dependencies.translateFn('Severity')}"
            labelfontweight="500"
            labelfontsize="12"
          >
            <gz-tooltip
              class="text-ellipsis-content has-bullet ${bullet}"
              tooltiptext="${severityLabel}"
              isellipsis="true"
            >
              ${htmlEncode(severityLabel)}
            </gz-tooltip>
          </gz-labeled-content>

          <gz-labeled-content
            label="${this.dependencies.translateFn('Endpoint name')}"
            labelfontweight="500"
            labelfontsize="12"
          >
            <gz-tooltip class="text-ellipsis-content" tooltiptext="${this.panelConfig.endpoint_name}" isellipsis="true">
              <div class="details-panel-endpoint-link">${endpointLink}</div>
            </gz-tooltip>
          </gz-labeled-content>

          <gz-labeled-content label="${this.dependencies.translateFn('User')}" labelfontweight="500" labelfontsize="12">
            <gz-tooltip class="text-ellipsis-content" tooltiptext="${this.panelConfig.user}" isellipsis="true">
              ${htmlEncode(this.panelConfig.user)}
            </gz-tooltip>
          </gz-labeled-content>

          <gz-labeled-content
            label="${this.dependencies.translateFn('Category')}"
            labelfontweight="500"
            labelfontsize="12"
          >
            <gz-tooltip class="text-ellipsis-content" tooltiptext="${this.panelConfig.category}" isellipsis="true">
              ${htmlEncode(this.panelConfig.category)}
            </gz-tooltip>
          </gz-labeled-content>
        </table>
      </div>
    `;
  }

  private renderDetailsSection(): TemplateResult {
    return html`
      <div class="details-panel-section">
        <gz-labeled-content
          class="details-panel-content-title"
          label="${this.dependencies.translateFn('Details')}"
          labelfontsize="11"
          labelfontweight="600"
          hasFormLabelWidth="true"
        ></gz-labeled-content>

        <table>
          <gz-labeled-content
            label="${this.dependencies.translateFn('Entity type')}"
            labelfontweight="500"
            labelfontsize="12"
          >
            <gz-tooltip class="text-ellipsis-content" tooltiptext="${this.panelConfig.entity_type}" isellipsis="true">
              ${htmlEncode(this.panelConfig.entity_type)}
            </gz-tooltip>
          </gz-labeled-content>

          <gz-labeled-content
            label="${this.dependencies.translateFn('Location')}"
            labelfontweight="500"
            labelfontsize="12"
            clampLine="true"
          >
            <gz-copy-to-clipboard
              showIconOnHover
              confirmationMessage="${this.copiedLabel}"
              tooltipText="${this.clipboardHoverLabel}"
            >
              <gz-tooltip class="text-ellipsis-content" tooltiptext="${this.panelConfig.key}" isellipsis="true">
                ${htmlEncode(this.panelConfig.key)}
              </gz-tooltip>
            </gz-copy-to-clipboard>
          </gz-labeled-content>

          <gz-labeled-content
            label="${this.dependencies.translateFn('Change')}"
            labelfontweight="500"
            labelfontsize="12"
          >
            <gz-tooltip class="text-ellipsis-content" tooltiptext="${this.panelConfig.change_type}" isellipsis="true">
              ${htmlEncode(this.panelConfig.change_type)}
            </gz-tooltip>
          </gz-labeled-content>

          ${this.getMitreIdsDetails()}
        </table>
      </div>
    `;
  }

  private getMitreIdsDetails(): TemplateResult {
    const mitreIds = this.panelConfig.mitre_ids ?? [];
    const label = mitreIds.length <= 1 ? translateFn('MITRE ATT&CK ID') : translateFn('MITRE ATT&CK IDs');
    const mitreIdsElements: Array<TemplateResult> = [];
    const tooltipText = mitreIds.join(', ');

    if (mitreIds.length === 0) {
      return html``;
    }

    mitreIds.forEach((mitreId, indexMitreId) => {
      const mitreURIResource = mitreId.replace('.', '/');
      const href = IMConstants.URL_ATTACK_MITRE_TECHNIQUES + '/' + mitreURIResource;
      const mitreIdLink = html`<gz-link href="${href}" fontSize="12" color="primary">${htmlEncode(mitreId)}</gz-link>`;
      mitreIdsElements.push(mitreIdLink);
      if (indexMitreId < mitreIds.length - 1) {
        mitreIdsElements.push(html`, `);
      }
    });

    return html`
      <div>
        <gz-labeled-content label="${label}" labelfontweight="500" labelfontsize="12">
          <gz-tooltip class="text-ellipsis-content" tooltiptext="${tooltipText}" isellipsis="true">
            ${mitreIdsElements}
          </gz-tooltip>
        </gz-labeled-content>
      </div>
    `;
  }

  private renderChangesSection(): TemplateResult {
    const changesAsHtml = [html``];
    const changesNames = getIntegrityMonitorChangesNames(this.dependencies.translateFn);

    // Render the rest of the changes
    for (const [changedElement, change] of Object.entries(this.panelConfig.changes)) {
      const formattedChangedElement = capitalize(changesNames[changedElement] || changedElement);
      const formattedChange = this.formatChangedAttributesValues(changedElement, change);

      // Push old value of the attribute
      changesAsHtml.push(html`
        <gz-labeled-content label="${formattedChangedElement + this.oldLabel}" labelfontweight="500" labelfontsize="12">
          <gz-copy-to-clipboard
            showIconOnHover
            confirmationMessage="${this.copiedLabel}"
            tooltipText="${this.clipboardHoverLabel}"
          >
            <gz-tooltip class="text-ellipsis-content" tooltiptext="${formattedChange.old}" isellipsis="true">
              ${htmlEncode(formattedChange.old)}
            </gz-tooltip>
          </gz-copy-to-clipboard>
        </gz-labeled-content>
      `);

      // Push new value of the attribute
      changesAsHtml.push(html`
        <gz-labeled-content label="${formattedChangedElement + this.newLabel}" labelfontweight="500" labelfontsize="12">
          <gz-copy-to-clipboard
            showIconOnHover
            confirmationMessage="${this.copiedLabel}"
            tooltipText="${this.clipboardHoverLabel}"
          >
            <gz-tooltip class="text-ellipsis-content" tooltiptext="${formattedChange.new}" isellipsis="true">
              ${htmlEncode(formattedChange.new)}
            </gz-tooltip>
          </gz-copy-to-clipboard>
        </gz-labeled-content>
      `);
    }

    if (isEmpty(this.panelConfig.changes)) {
      return html``;
    }

    return html`
      <div class="details-panel-section">
        <gz-labeled-content
          class="details-panel-content-title"
          label="${this.dependencies.translateFn('Attribute changes')}"
          labelfontsize="11"
          labelfontweight="600"
          hasFormLabelWidth="true"
        ></gz-labeled-content>

        <table>
          ${changesAsHtml}
        </table>
      </div>
    `;
  }

  private renderActionsSection(): TemplateResult {
    const actionResultsHtml = [html``];
    const attributeNames = getIntegrityMonitorChangesNames(this.dependencies.translateFn);
    const actionNames = getIntegrityMonitorRulesActionsLabels(this.dependencies.translateFn);
    const actionResults = getIntegrityMonitorResultTexts(this.dependencies.translateFn);

    if (isEmpty(this.panelConfig.actions_result)) {
      return html``;
    }

    for (const action_result of this.panelConfig.actions_result) {
      const attribute = capitalize(attributeNames[action_result.attribute] || action_result.attribute);
      const actionName = actionNames[action_result.action] || action_result.action;
      const actionResult = actionResults[action_result.result] || action_result.result;

      const accordionComponent = new GzAccordionWebComponent();
      accordionComponent.accordionTitle = attribute;

      // Push the action's name
      accordionComponent.innerHTML += `
        <gz-labeled-content
        label="${this.dependencies.translateFn('Action')}"
        labelfontweight="500"
        labelfontsize="12"
        >
          ${htmlEncode(actionName)}
        </gz-labeled-content>
      `;

      // Push the action result
      accordionComponent.innerHTML += `
          <gz-labeled-content
            label="${this.dependencies.translateFn('Result')}"
            labelfontweight="500"
            labelfontsize="12"
          >
            ${htmlEncode(actionResult)}
          </gz-labeled-content>
        `;

      actionResultsHtml.push(html`${accordionComponent}`);
    }

    return html`
      <div class="details-panel-section">
        <gz-labeled-content
          class="details-panel-content-title"
          label="${this.dependencies.translateFn('Actions taken')}"
          labelfontsize="11"
          labelfontweight="600"
          hasFormLabelWidth="true"
        ></gz-labeled-content>

        <table>
          ${actionResultsHtml}
        </table>
      </div>
    `;
  }

  private sendEndpointShowEventFromPanel() {
    this.dispatchEvent(
      new CustomEvent(CommonEvents.CLICK_ON_ENDPOINT_NAME, {
        detail: {
          endpointId: this.panelConfig.endpoint_id,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  private formatChangedAttributesValues(
    changedElement: string,
    change: IntegrityMonitorChangedAttribute<any>
  ): IntegrityMonitorChangedAttribute<any> {
    const locale = this.dependencies.getUserLanguage();

    switch (changedElement) {
      case 'last_modified':
        const timezone = this.dependencies.getTimezone();
        const oldDateFormatted = getFormattedDateTime(TimestampFormat.EVENTS, change.old, timezone, locale);
        const newDateFormatted = getFormattedDateTime(TimestampFormat.EVENTS, change.new, timezone, locale);

        return {
          old: oldDateFormatted,
          new: newDateFormatted,
        };

      case 'subkeys':
      case 'values':
        return {
          old: change.old.join(', '),
          new: change.new.join(', '),
        };

      case 'installed_date':
        const defaultTimezone = 'UTC';
        const oldDateTimestamp = Date.UTC(
          change.old.substring(0, 4),
          change.old.substring(4, 6) - 1,
          change.old.substring(6)
        );
        const newDateTimestamp = Date.UTC(
          change.new.substring(0, 4),
          change.new.substring(4, 6) - 1,
          change.new.substring(6)
        );

        return {
          old: getFormattedDateTime(TimestampFormat.DATE_ONLY, oldDateTimestamp, defaultTimezone, locale),
          new: getFormattedDateTime(TimestampFormat.DATE_ONLY, newDateTimestamp, defaultTimezone, locale),
        };

      case 'start_type':
        const startTypeMapping = getImAttributeChangesStartTypeMapping(this.dependencies.translateFn);

        return {
          old: startTypeMapping[change.old] || change.old,
          new: startTypeMapping[change.new] || change.new,
        };

      case 'status':
        const statusMapping = getImAttributeChangesStatusMapping(this.dependencies.translateFn);

        return {
          old: statusMapping[change.old] || change.old,
          new: statusMapping[change.new] || change.new,
        };
    }

    return change;
  }
}
